import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Icon } from '../icon/icon';

/**
 * Reads one of the site's own legal documents without leaving the page.
 *
 * The documents are standalone files under `public/` — see
 * `public/privacy-en.html` for why. This dialog does not restate them: it
 * fetches the file the link points at and shows the `<main>` of it, so the
 * dialog and the page at that URL can never say different things.
 *
 * The link stays a real link. Opening the dialog is an enhancement layered on
 * top of it, so the document is still reachable with scripting off, in a new
 * tab, and by anything that only reads `href`.
 */
@Component({
  selector: 'av-legal-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon, TranslatePipe],
  template: `
    <dialog
      #frame
      class="ld"
      [attr.aria-label]="label()"
      (cancel)="onCancel($event)"
      (click)="onSurfaceClick($event)"
    >
      <div class="ld__panel">
        <button
          type="button"
          class="ld__close"
          [attr.aria-label]="'a11y.documentClose' | translate"
          (click)="requestClose()"
        >
          <av-icon name="close" [size]="18" />
        </button>

        <div class="ld__body" tabindex="0">
          @switch (state()) {
            @case ('loading') {
              <p class="ld__status">{{ 'legal.loading' | translate }}</p>
            }
            @case ('failed') {
              <p class="ld__status">
                {{ 'legal.dialogFailed' | translate }}
                <a [href]="href()">{{ 'legal.dialogOpenPage' | translate }}</a>
              </p>
            }
            @default {
              <!-- Angular sanitises this on the way in. The source is a file in
                   this repository served from this origin and carries no script,
                   so nothing of substance is stripped. -->
              <div class="ld__doc" [innerHTML]="body()"></div>
            }
          }
        </div>
      </div>
    </dialog>
  `,
  styleUrl: './legal-dialog.scss',
})
export class LegalDialog {
  private readonly destroyRef = inject(DestroyRef);

  /** The document's own URL — the same one the link points at. */
  readonly href = input.required<string>();
  readonly open = input.required<boolean>();
  readonly label = input<string>('');

  readonly closed = output<void>();

  private readonly frame = viewChild<ElementRef<HTMLDialogElement>>('frame');

  protected readonly state = signal<'loading' | 'ready' | 'failed'>('loading');
  protected readonly body = signal('');

  /** One fetch per document, kept for as long as the footer lives. */
  private readonly cache = new Map<string, string>();
  private pushedState = false;
  private pageOffset = 0;

  constructor() {
    effect(() => (this.open() ? this.show() : this.hide()));
    this.destroyRef.onDestroy(() => this.detach());
  }

  protected onCancel(event: Event): void {
    // Esc would close the dialog without unwinding the history entry we pushed.
    event.preventDefault();
    this.requestClose();
  }

  protected onSurfaceClick(event: MouseEvent): void {
    // The panel fills the dialog, so a click that lands on the dialog itself
    // landed on the backdrop.
    if (event.target === this.frame()?.nativeElement) this.requestClose();
  }

  protected requestClose(): void {
    if (this.pushedState) {
      history.back();
      return;
    }
    this.finish();
  }

  private show(): void {
    const frame = this.frame()?.nativeElement;
    if (!frame || frame.open) return;

    void this.load(this.href());

    this.pageOffset = window.scrollY;
    frame.showModal();
    document.documentElement.classList.add('lb-open');

    history.pushState({ ...history.state, avLegalDocument: true }, '');
    this.pushedState = true;
    window.addEventListener('popstate', this.onPopState);
  }

  /** Takes the document's `<main>`: its header and footer are the page's, not the dialog's. */
  private async load(href: string): Promise<void> {
    const cached = this.cache.get(href);
    if (cached) {
      this.body.set(cached);
      this.state.set('ready');
      return;
    }

    this.state.set('loading');
    try {
      const response = await fetch(href);
      if (!response.ok) throw new Error(String(response.status));

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const main = parsed.querySelector('main');
      if (!main) throw new Error('no main');

      this.cache.set(href, main.innerHTML);
      this.body.set(main.innerHTML);
      this.state.set('ready');
    } catch {
      // The link under the dialog still works; the message says so.
      this.state.set('failed');
    }
  }

  private hide(): void {
    if (this.frame()?.nativeElement.open) this.requestClose();
  }

  private readonly onPopState = (): void => {
    this.pushedState = false;
    this.finish();
  };

  private finish(): void {
    this.detach();
    const frame = this.frame()?.nativeElement;
    if (frame?.open) frame.close();
    this.closed.emit();
    this.restorePageOffset();
  }

  private restorePageOffset(): void {
    const top = this.pageOffset;
    // `behavior: instant` because the document scrolls smoothly by default.
    const apply = () => window.scrollTo({ top, left: 0, behavior: 'instant' });
    apply();
    requestAnimationFrame(apply);
  }

  private detach(): void {
    // The dialog is a browser-only surface, but the prerender destroys this
    // component too — and there is no window there to take the listener off.
    if (typeof window === 'undefined') return;

    window.removeEventListener('popstate', this.onPopState);
    document.documentElement.classList.remove('lb-open');
  }
}
